import { DefaultTheme, ThemeProps } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getToken } from '@optimus-web/common';

export type Variants = 'checked' | 'unChecked';
export type States = 'disabled' | 'default';

interface RadioProps {
    checked?: boolean;
    disabled?: boolean;
}

export type ColorTokens<T extends string, K extends string> = {
    [variant in T]: {
        [state in K]?: ((props: ThemeProps<DefaultTheme>) => string) | string;
    };
};

export const getVariantAndState = (props: ThemeProps<DefaultTheme> & RadioProps) => {
    const variant: Variants = props?.checked ? 'checked' : 'unChecked';
    const state: States = props?.disabled ? 'disabled' : 'default';
    return { variant, state };
};

export const getBackgroundColor = (props: ThemeProps<DefaultTheme>) => {
    const getBackgroundColorTokens: ColorTokens<Variants, States> = {
        checked: {
            disabled: getToken(TokenName.BACKGROUND_DISABLED),
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_PRIMARY_DEFAULT),
        },
        unChecked: {
            disabled: 'inherit',
            default: 'inherit',
        },
    };

    const { variant, state } = getVariantAndState(props);
    return getBackgroundColorTokens[variant][state];
};

export const getBorderColor = (props: ThemeProps<DefaultTheme>) => {
    const getBorderColorTokens: ColorTokens<Variants, States> = {
        checked: {
            disabled: getToken(TokenName.BACKGROUND_DISABLED),
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_PRIMARY_DEFAULT),
        },
        unChecked: {
            disabled: getToken(TokenName.BORDER_DISABLED),
            default: getToken(TokenName.BORDER_INTERACTIVE_SECONDARY_DEFAULT),
        },
    };

    const { variant, state } = getVariantAndState(props);
    return getBorderColorTokens[variant][state];
};

export const getBorderColorHover = (props: ThemeProps<DefaultTheme>) => {
    const getBorderColorHoverTokens: ColorTokens<Variants, States> = {
        checked: {
            disabled: getToken(TokenName.BACKGROUND_DISABLED),
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_PRIMARY_HOVER),
        },
        unChecked: {
            disabled: getToken(TokenName.BORDER_DISABLED),
            default: getToken(TokenName.BORDER_INTERACTIVE_SECONDARY_HOVER),
        },
    };

    const { variant, state } = getVariantAndState(props);
    return getBorderColorHoverTokens[variant][state];
};

export const getBackgroundColorHover = (props: ThemeProps<DefaultTheme>) => {
    const getBackgroundColorHoverTokens: ColorTokens<Variants, States> = {
        checked: {
            disabled: getToken(TokenName.BACKGROUND_DISABLED),
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_PRIMARY_HOVER),
        },
        unChecked: {
            disabled: 'inherit',
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_HOVER),
        },
    };

    const { variant, state } = getVariantAndState(props);
    return getBackgroundColorHoverTokens[variant][state];
};

export const getBorderColorActive = (props: ThemeProps<DefaultTheme>) => {
    const getBorderColorActiveTokens: ColorTokens<Variants, States> = {
        checked: {
            disabled: getToken(TokenName.BACKGROUND_DISABLED),
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_PRIMARY_ACTIVE),
        },
        unChecked: {
            disabled: getToken(TokenName.BORDER_DISABLED),
            default: getToken(TokenName.BORDER_INTERACTIVE_SECONDARY_ACTIVE),
        },
    };

    const { variant, state } = getVariantAndState(props);
    return getBorderColorActiveTokens[variant][state];
};

export const getBackgroundColorActive = (props: ThemeProps<DefaultTheme>) => {
    const getBackgroundColorActiveTokens: ColorTokens<Variants, States> = {
        checked: {
            disabled: getToken(TokenName.BACKGROUND_DISABLED),
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_PRIMARY_ACTIVE),
        },
        unChecked: {
            disabled: 'inherit',
            default: getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_ACTIVE),
        },
    };

    const { variant, state } = getVariantAndState(props);
    return getBackgroundColorActiveTokens[variant][state];
};
